import { DEFAULT_PATCH } from "./defaults";
import type {
  CRTVarPatch,
  ModulationRoute,
  OscillatorSettings,
  ParameterId,
  SequencerSettings,
  SequencerStep,
  SourceFit,
  SourceKind,
  SynthSettings,
  VideoSettings,
  Waveform,
} from "./types";
import { PARAMETER_BY_ID } from "./catalog";

export const PATCH_STORAGE_KEY = "crtvar:patches:v3";
export const SESSION_STORAGE_KEY = "crtvar:current-session:v3";
export const LEGACY_PATCH_STORAGE_KEYS = ["crtvar:patches:v2", "crtvar:patches:v1"] as const;
export const LEGACY_SESSION_STORAGE_KEYS = ["crtvar:current-session:v2", "crtvar:current-session:v1"] as const;
export const MAX_PATCH_BYTES = 256 * 1024;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const finite = (value: unknown, fallback: number) =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

type NumericKeys<T> = {
  [Key in keyof T]-?: T[Key] extends number ? Key : never;
}[keyof T];

interface NumericRule {
  readonly min: number;
  readonly max: number;
  readonly integer?: boolean;
}

const WAVEFORMS = ["sine", "triangle", "saw", "square"] as const satisfies readonly Waveform[];
const ENGINE_MODES = ["image", "composite"] as const satisfies readonly VideoSettings["mode"][];
const SOURCE_FITS = ["fit", "fill"] as const satisfies readonly SourceFit[];
const SOURCE_KINDS = ["bars", "grid", "scene", "image", "video", "camera"] as const satisfies readonly SourceKind[];

const parameterRule = (id: ParameterId): NumericRule => {
  const descriptor = PARAMETER_BY_ID.get(id);
  if (!descriptor) throw new Error(`Missing parameter descriptor for ${id}.`);
  return { min: descriptor.min, max: descriptor.max };
};

const VIDEO_NUMERIC_RULES = {
  horizontalJitter: parameterRule("video.horizontalJitter"),
  verticalRoll: parameterRule("video.verticalRoll"),
  chromaPhase: parameterRule("video.chromaPhase"),
  chromaGain: parameterRule("video.chromaGain"),
  noise: parameterRule("video.noise"),
  feedback: parameterRule("video.feedback"),
  burstAmplitude: parameterRule("video.burstAmplitude"),
  linePosition: parameterRule("video.linePosition"),
  lineWidth: parameterRule("video.lineWidth"),
  gain: parameterRule("video.gain"),
  offset: parameterRule("video.offset"),
  syncOffset: parameterRule("video.syncOffset"),
  clipping: parameterRule("video.clipping"),
  solarize: parameterRule("video.solarize"),
  receiverTracking: { min: 0, max: 1 },
  receiverBandwidth: { min: 0, max: 1 },
  scopeLine: { min: 0, max: 524, integer: true },
} satisfies Record<NumericKeys<VideoSettings>, NumericRule>;

const SYNTH_NUMERIC_RULES = {
  noiseLevel: parameterRule("synth.noiseLevel"),
  cutoff: parameterRule("synth.cutoff"),
  resonance: parameterRule("synth.resonance"),
  attack: parameterRule("synth.attack"),
  decay: parameterRule("synth.decay"),
  sustain: parameterRule("synth.sustain"),
  release: parameterRule("synth.release"),
  lfoRate: parameterRule("synth.lfoRate"),
  drive: parameterRule("synth.drive"),
} satisfies Record<NumericKeys<SynthSettings>, NumericRule>;

const OSCILLATOR_ONE_NUMERIC_RULES = {
  octave: { min: -3, max: 3, integer: true },
  detune: parameterRule("synth.osc1Detune"),
  level: parameterRule("synth.osc1Level"),
} satisfies Record<NumericKeys<OscillatorSettings>, NumericRule>;

const OSCILLATOR_TWO_NUMERIC_RULES = {
  octave: { min: -3, max: 3, integer: true },
  detune: parameterRule("synth.osc2Detune"),
  level: parameterRule("synth.osc2Level"),
} satisfies Record<NumericKeys<OscillatorSettings>, NumericRule>;

const SEQUENCER_NUMERIC_RULES = {
  bpm: { min: 40, max: 240 },
  swing: { min: 0, max: 0.6 },
} satisfies Record<NumericKeys<SequencerSettings>, NumericRule>;

const STEP_NUMERIC_RULES = {
  note: { min: 0, max: 127, integer: true },
  velocity: { min: 0, max: 1 },
  gate: { min: 0.05, max: 1 },
} satisfies Record<NumericKeys<SequencerStep>, NumericRule>;

export function normalizePatch(candidate: unknown): CRTVarPatch {
  if (!isRecord(candidate) || candidate.format !== "crtvar-patch") {
    throw new Error("Not a CRTVAR patch.");
  }
  const version = finite(candidate.schemaVersion, 1);
  if (!Number.isInteger(version) || version < 1) throw new Error(`Invalid patch schema ${version}.`);
  if (version > 3) throw new Error(`Patch schema ${version} is newer than this instrument.`);

  const migrated = migratePatch(candidate, version);
  const patch = structuredClone(DEFAULT_PATCH);
  patch.name = typeof migrated.name === "string" && migrated.name.trim() ? migrated.name.slice(0, 80) : "Imported patch";
  patch.seed = finite(migrated.seed, patch.seed) >>> 0;

  if (isRecord(migrated.source)) {
    patch.source.kind = coerceEnum(migrated.source.kind, SOURCE_KINDS, patch.source.kind);
    patch.source.fit = coerceEnum(migrated.source.fit, SOURCE_FITS, patch.source.fit);
    if (typeof migrated.source.displayName === "string") patch.source.displayName = migrated.source.displayName.slice(0, 120);
  }

  if (isRecord(migrated.synth)) patch.synth = normalizeSynth(patch.synth, migrated.synth);
  if (isRecord(migrated.video)) patch.video = normalizeVideo(patch.video, migrated.video);
  if (isRecord(migrated.keyboard)) {
    patch.keyboard.octave = clamp(Math.round(finite(migrated.keyboard.octave, patch.keyboard.octave)), 0, 7);
    patch.keyboard.capture = migrated.keyboard.capture !== false;
  }
  if (isRecord(migrated.sequencer)) {
    const sequencer = migrated.sequencer;
    patch.sequencer.enabled = sequencer.enabled === true;
    patch.sequencer = normalizeNumericFields(patch.sequencer, sequencer, SEQUENCER_NUMERIC_RULES);
    const sequenceSteps = sequencer.steps;
    if (Array.isArray(sequenceSteps)) {
      patch.sequencer.steps = patch.sequencer.steps.map((fallback, index) => {
        const item = sequenceSteps[index];
        if (!isRecord(item)) return fallback;
        const normalized = normalizeNumericFields(fallback, item, STEP_NUMERIC_RULES);
        normalized.enabled = typeof item.enabled === "boolean" ? item.enabled : fallback.enabled;
        return normalized;
      });
    }
  }

  patch.modulationRoutes = normalizeRoutes(migrated.modulationRoutes);
  patch.midiMappings = Array.isArray(migrated.midiMappings)
    ? migrated.midiMappings.flatMap((mapping) => {
        if (!isRecord(mapping) || !PARAMETER_BY_ID.has(mapping.target as ParameterId)) return [];
        const channel: number | "all" = mapping.channel === "all" ? "all" : clamp(Math.round(finite(mapping.channel, 0)), 0, 15);
        return [{ channel, cc: clamp(Math.round(finite(mapping.cc, 0)), 0, 127), target: mapping.target as ParameterId }];
      }).slice(0, 32)
    : [];
  patch.schemaVersion = 3;
  return patch;
}

function normalizeNumericFields<T extends object>(
  fallback: T,
  input: Record<string, unknown>,
  rules: Readonly<Partial<Record<keyof T, NumericRule>>>,
): T {
  const result = structuredClone(fallback) as Record<string, unknown>;
  for (const [key, rule] of Object.entries(rules) as Array<[string, NumericRule]>) {
    const fallbackValue = result[key];
    if (typeof fallbackValue !== "number") continue;
    const value = finite(input[key], fallbackValue);
    result[key] = clamp(rule.integer ? Math.round(value) : value, rule.min, rule.max);
  }
  return result as T;
}

function normalizeSynth(fallback: SynthSettings, input: Record<string, unknown>): SynthSettings {
  const result = normalizeNumericFields(fallback, input, SYNTH_NUMERIC_RULES);
  result.lfoWaveform = coerceEnum(input.lfoWaveform, WAVEFORMS, fallback.lfoWaveform);
  if (isRecord(input.osc1)) {
    result.osc1 = normalizeNumericFields(fallback.osc1, input.osc1, OSCILLATOR_ONE_NUMERIC_RULES);
    result.osc1.waveform = coerceEnum(input.osc1.waveform, WAVEFORMS, fallback.osc1.waveform);
  }
  if (isRecord(input.osc2)) {
    result.osc2 = normalizeNumericFields(fallback.osc2, input.osc2, OSCILLATOR_TWO_NUMERIC_RULES);
    result.osc2.waveform = coerceEnum(input.osc2.waveform, WAVEFORMS, fallback.osc2.waveform);
  }
  return result;
}

function normalizeVideo(fallback: VideoSettings, input: Record<string, unknown>): VideoSettings {
  const result = normalizeNumericFields(fallback, input, VIDEO_NUMERIC_RULES);
  result.mode = coerceEnum(input.mode, ENGINE_MODES, fallback.mode);
  return result;
}

function coerceEnum<const Value extends string>(
  candidate: unknown,
  values: readonly Value[],
  fallback: Value,
): Value {
  return typeof candidate === "string" && (values as readonly string[]).includes(candidate)
    ? candidate as Value
    : fallback;
}

function normalizeRoutes(input: unknown): ModulationRoute[] {
  if (!Array.isArray(input)) return [];
  const usedIds = new Set<string>();
  return input.flatMap((route, index) => {
    if (!isRecord(route) || !PARAMETER_BY_ID.has(route.target as ParameterId)) return [];
    const allowedSources = ["lfo1", "lfo2", "envelope", "notePitch", "noteVelocity", "audioRms", "audioLow", "audioMid", "audioHigh", "noise", "sequencer", "expression"];
    if (!allowedSources.includes(String(route.source))) return [];
    return [{
      id: uniqueRouteId(route.id, index, usedIds),
      source: route.source as ModulationRoute["source"],
      target: route.target as ParameterId,
      depth: clamp(finite(route.depth, 0), -1, 1),
      smoothingMs: clamp(finite(route.smoothingMs, 0), 0, 5000),
      enabled: route.enabled !== false,
    }];
  }).slice(0, 8);
}

function uniqueRouteId(candidate: unknown, index: number, usedIds: Set<string>): string {
  const cleaned = typeof candidate === "string"
    ? candidate.replace(/[\u0000-\u001f\u007f]/g, "").trim().slice(0, 80)
    : "";
  const base = cleaned || `route-${index}`;
  let id = base;
  let suffix = 2;
  while (usedIds.has(id)) {
    const marker = `-${suffix}`;
    id = `${base.slice(0, 80 - marker.length)}${marker}`;
    suffix += 1;
  }
  usedIds.add(id);
  return id;
}

function migratePatch(input: Record<string, unknown>, version: number): Record<string, unknown> {
  let migrated = structuredClone(input);
  if (version <= 1) {
    migrated = {
      ...migrated,
      sequencer: structuredClone(DEFAULT_PATCH.sequencer),
      midiMappings: [],
      schemaVersion: 2,
    };
  }
  if (version <= 2) {
    const video = isRecord(migrated.video) ? migrated.video : {};
    migrated = {
      ...migrated,
      engine: { modelRevision: "ntsc-m-1" },
      video: { ...structuredClone(DEFAULT_PATCH.video), ...video },
      schemaVersion: 3,
    };
  }
  return migrated;
}

export function parsePatchText(text: string): CRTVarPatch {
  if (new TextEncoder().encode(text).byteLength > MAX_PATCH_BYTES) throw new Error("Patch exceeds the 256 KiB import limit.");
  return normalizePatch(JSON.parse(text) as unknown);
}

export function serializePatch(patch: CRTVarPatch): string {
  return `${JSON.stringify(normalizePatch(patch), null, 2)}\n`;
}

export function loadStoredPatches(): CRTVarPatch[] {
  try {
    const sourceKey = [PATCH_STORAGE_KEY, ...LEGACY_PATCH_STORAGE_KEYS].find((key) => localStorage.getItem(key) !== null);
    const raw = sourceKey ? localStorage.getItem(sourceKey) : null;
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    const normalized = Array.isArray(parsed) ? parsed.flatMap((value) => {
      try { return [normalizePatch(value)]; } catch { return []; }
    }) : [];
    if (sourceKey && sourceKey !== PATCH_STORAGE_KEY) localStorage.setItem(PATCH_STORAGE_KEY, JSON.stringify(normalized));
    return normalized;
  } catch {
    return [];
  }
}
